'use client';

import { Box, Typography, useMediaQuery } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { v4 as uuid } from 'uuid';
import { useAnalytics } from '@repo/segment/Analytics';
import { useClientOnlyConstant } from '@repo/segment/hooks/useClientOnlyConstant';
import { shouldShowPoster } from '@repo/supergraph/structuredContent/hydration/WpVideo/thumbnails';
import type { Video } from '@repo/supergraph/structuredContent/hydration/WpVideo/types';
import type { StructuredContentDocument } from '@repo/supergraph/structuredContent/types';
import { theme } from '@repo/ui/NerdWalletThemeProvider';
import { VideoLinkedData } from '../../../LinkedDataJson';
import { PlayMediaIcon } from './PlayMediaIcon';
import type { IVideoOptions, SupportedServices } from './types';
import { getVideoImageSRC, getVideoImageSRCSet, getVideoUrl } from './utils';

export type VideoSCProps = {
  video: Video;
  service: SupportedServices;
  options?: IVideoOptions;
  shouldRenderJsonLd?: boolean;
};

type WPVideoProps = StructuredContentDocument<VideoSCProps>;

export function WPVideo({ props }: WPVideoProps) {
  const {
    options = { showPreview: true, showRelatedVideos: false },
    video,
    shouldRenderJsonLd = true,
  } = props;
  const showPoster = shouldShowPoster(
    options.showPreview,
    video?.youtubeVideo?.snippet?.thumbnails,
  );
  const [showPreview, setShowPreview] = useState(showPoster);
  const [isVisible, setIsVisible] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const intervalRef = useRef<NodeJS.Timeout>(undefined);
  const eventFiredRef = useRef<boolean>(false);
  const impressionId = useClientOnlyConstant(() => uuid(), '');

  const { analytics, deployableName, gitSha, deviceId } = useAnalytics();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const segmentInstance = analytics?.segmentInstance;

  const videoId = video?.youtubeVideo?.id;

  let timeLength = options.timeLength;
  if (!timeLength?.match(/^(([0]?[0-5][0-9]|[0-9]):([0-5][0-9]))$/)) {
    timeLength = undefined;
  }

  const fireInteraction = () => {
    eventFiredRef.current = true;

    if (segmentInstance) {
      segmentInstance.track(
        'Feature Interacted',
        {
          entity_name: 'video',
          section_name: null,
          item_position: null,
          item_copy: null,
          event_id: uuid(),
          content_id: videoId,
          parent_event_id: impressionId,
          additional_attributes: [],
        },
        {
          context: {
            app: {
              name: deployableName,
              version: gitSha,
            },
          },
        },
      );
    }
  };

  const hasVideo = Boolean(video?.youtubeVideo);

  useEffect(() => {
    if (!hasVideo) return;

    intervalRef.current = setInterval(() => {
      if (eventFiredRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = undefined;
        return;
      }
      if (!document?.activeElement) return;
      if (document.activeElement === iframeRef.current) {
        fireInteraction();
      }
    }, 250);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [hasVideo]);

  useEffect(() => {
    if (deviceId && isVisible && segmentInstance) {
      void segmentInstance.track(
        'Feature Viewed',
        {
          type: 'track',
          segmentAttributes: {
            entity_name: 'video',
            section_name: null,
            item_position: null,
            item_copy: null,
            content_id: videoId,
            event_id: impressionId,
          },
        },
        {
          context: {
            app: {
              name: deployableName,
              version: gitSha,
            },
          },
        },
      );
    }
  }, [deviceId, isVisible]);

  if (!video || !video.youtubeVideo) return null;

  const handleViewportEntering = (inView: boolean) => {
    if (inView) setIsVisible(true);
  };

  const playButtonClickHandler = () => {
    setShowPreview(false);
    fireInteraction();
  };

  return (
    <InView triggerOnce onChange={handleViewportEntering}>
      <Box sx={{ position: 'relative', height: 0, paddingBottom: '56.25%', overflow: 'hidden' }}>
        {showPreview ? (
          <Box
            sx={{
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignContent: 'center',
              overflow: 'hidden',
              position: 'relative',
              alignItems: 'center',
            }}
            onClick={playButtonClickHandler}
            onKeyUp={playButtonClickHandler}
            role="button"
            tabIndex={0}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                cursor: 'pointer',
                opacity: 0.9,
                position: 'absolute',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: { xs: '70px', sm: '96px' },
                height: { xs: '70px', sm: '96px' },
                borderRadius: 100,
                zIndex: 1,
                border: 0,
              }}
              aria-label="Play"
              role="button"
            >
              <PlayMediaIcon
                color={theme.palette.blues.main}
                size={isMobile ? '44px' : '56px'}
                style={{
                  marginLeft: '4px',
                  marginTop: isMobile ? '7px' : '2px',
                }}
              />
            </Box>
            {timeLength && (
              <Box
                sx={{
                  backgroundColor: 'black',
                  width: 'auto',
                  height: 'auto',
                  textAlign: 'center',
                  position: 'absolute',
                  paddingX: '1rem',
                  paddingY: '0.5rem',
                  justifyContent: 'center',
                  zIndex: '1',
                  bottom: { xs: 2, sm: 3 },
                  right: { xs: 2, sm: 3 },
                }}
              >
                <Typography>{timeLength}</Typography>
              </Box>
            )}
            {/** Using img native component for using srcSet attribute */}
            <img
              alt="Video preview image"
              loading="lazy"
              style={{
                objectFit: 'cover',
                width: '100%',
                height: '100%',
              }}
              src={getVideoImageSRC(video)}
              srcSet={getVideoImageSRCSet(video)}
            />
          </Box>
        ) : (
          <iframe
            title={video?.youtubeVideo?.snippet?.title}
            ref={iframeRef}
            src={getVideoUrl(videoId, showPoster ? options : { ...options, showPreview: false })}
            width="100%"
            height="100%"
            style={{ border: 0, borderWidth: '0px', top: 0, left: 0, position: 'absolute' }}
            allow="autoplay"
            allowFullScreen
          />
        )}
        {shouldRenderJsonLd && <VideoLinkedData id="video-linked-data" video={video} />}
      </Box>
    </InView>
  );
}
